// Iter v34cf — Halaman Face-Scan Attendance Kiosk mode.
// Perangkat bersama (tablet/PC di plant) yang otomatis mendeteksi wajah karyawan
// dan melakukan clock-in/out tanpa harus login sebagai karyawan itu sendiri.
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ScanFace, MapPin, RefreshCw, Play, Pause, CheckCircle2, XCircle, Clock, LogIn, LogOut, AlertTriangle, Volume2, VolumeX, Maximize2, Minimize2, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button, Badge } from "@/components/ui-kit";
import { loadFaceApi, getCamera, stopStream, detectFace, snapshotVideo, IS_LOW_END } from "@/lib/faceApi";
import { speakVoice, VOICE_PHRASES, warmupVoices } from "@/lib/voice";

async function getGPSOnce() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Browser tidak mendukung GPS"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  });
}

// Iter v34cg — Simple WebAudio beep. Tidak butuh asset file.
function playTone(ctx, { freq = 800, duration = 180, type = "sine", volume = 0.15 } = {}) {
  if (!ctx) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration / 1000 + 0.02);
  } catch { /* ignore */ }
}

async function playPattern(ctx, pattern) {
  for (const step of pattern) {
    playTone(ctx, step);
    // Serialize: wait step duration + small gap
    await new Promise((r) => setTimeout(r, (step.duration || 150) + 30));
  }
}

export default function AbsenKiosk() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [faReady, setFaReady] = useState(false);
  const [faError, setFaError] = useState(null);
  const [statusMsg, setStatusMsg] = useState("Memuat model wajah…");
  const [running, setRunning] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [gps, setGps] = useState(null);
  const [lastResult, setLastResult] = useState(null); // { ok, name, action, ts, detail }
  const cooldownRef = useRef({}); // { emp_id: unix_ts_ok_after }
  const lastScanRef = useRef(0);
  // Iter v34cg — Audio notification toggle. Default true; disimpan di localStorage.
  const [audioOn, setAudioOn] = useState(() => {
    try { return localStorage.getItem("kiosk_audio_on") !== "false"; } catch { return true; }
  });
  // Iter v34cj — Volume TTS voice (0..1). Persist localStorage.
  const [audioVolume, setAudioVolume] = useState(() => {
    try { return Math.min(1, Math.max(0, parseFloat(localStorage.getItem("kiosk_audio_volume") || "1") || 1)); }
    catch { return 1; }
  });
  useEffect(() => { warmupVoices(); }, []);
  const audioCtxRef = useRef(null);
  // Iter v34ci — Fullscreen + PIN lock
  const [pinRequired, setPinRequired] = useState(false); // apakah backend punya PIN?
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [locked, setLocked] = useState(false); // overlay lock aktif
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinChecking, setPinChecking] = useState(false);
  useEffect(() => {
    // Check apakah PIN sudah di-set di server. Kalau belum, tombol fullscreen tetap tersedia
    // tapi tidak lock saat exit.
    api.get("/settings").then((r) => setPinRequired(!!r.data?.kiosk_pin_set)).catch(() => {});
  }, []);
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  // Auto-lock ketika tab tersembunyi (user pindah app) — hanya kalau kiosk running.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden && running && pinRequired && !locked) {
        setLocked(true);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [running, pinRequired, locked]);
  // Saat keluar fullscreen (Esc), langsung lock jika PIN diaktifkan.
  useEffect(() => {
    if (!isFullscreen && running && pinRequired && !locked) {
      setLocked(true);
    }
  }, [isFullscreen, running, pinRequired, locked]);
  const enterFullscreen = async () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: "hide" });
    } catch (e) { /* ignore */ }
  };
  const requestExitFullscreen = () => {
    // Jika PIN diaktifkan → lock dulu; user harus PIN untuk keluar.
    if (pinRequired) {
      setLocked(true);
      return;
    }
    try { if (document.fullscreenElement) document.exitFullscreen(); } catch (e) { /* ignore */ }
  };
  const submitPin = async () => {
    if (!/^\d{6}$/.test(pinInput)) { setPinError("PIN harus 6 digit"); return; }
    setPinChecking(true); setPinError("");
    try {
      const r = await api.post("/kiosk/verify-pin", { pin: pinInput });
      if (r.data?.ok) {
        setLocked(false);
        setPinInput("");
        // Kalau user memang mau exit fullscreen, keluarkan sekarang.
        try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (e) { /* ignore */ }
      } else {
        setPinError(r.data?.detail || "PIN salah");
      }
    } catch (e) { setPinError(e?.response?.data?.detail || "Gagal verifikasi"); }
    finally { setPinChecking(false); }
  };
  useEffect(() => {
    try { localStorage.setItem("kiosk_audio_on", audioOn ? "true" : "false"); } catch { /* ignore */ }
  }, [audioOn]);
  useEffect(() => {
    try { localStorage.setItem("kiosk_audio_volume", String(audioVolume)); } catch { /* ignore */ }
  }, [audioVolume]);
  const ensureAudioCtx = useCallback(() => {
    if (!audioOn) return null;
    if (!audioCtxRef.current) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = AC ? new AC() : null;
      } catch { audioCtxRef.current = null; }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  }, [audioOn]);
  const playSound = useCallback((kind) => {
    if (!audioOn) return;
    // Iter v34cj — TTS voice notifications (English female preferred)
    if (kind === "success_in") speakVoice(VOICE_PHRASES.granted, { volume: audioVolume });
    else if (kind === "success_out") speakVoice(VOICE_PHRASES.thankyou, { volume: audioVolume });
    else if (kind === "already") speakVoice(VOICE_PHRASES.already, { volume: audioVolume });
    else if (kind === "unknown") speakVoice(VOICE_PHRASES.denied, { volume: audioVolume });
    else if (kind === "cooldown") speakVoice(VOICE_PHRASES.already, { volume: audioVolume });
    // Fallback: tetap init audio ctx supaya beep toggle test masih bunyi
    ensureAudioCtx();
  }, [audioOn, audioVolume, ensureAudioCtx]);

  const initModel = useCallback(async () => {
    setFaError(null); setFaReady(false); setStatusMsg("Memuat model wajah…");
    try {
      await loadFaceApi((m) => setStatusMsg(m));
      setFaReady(true); setStatusMsg("Model siap");
    } catch (e) {
      setFaError(e.message || "Gagal load face-api"); setStatusMsg("");
    }
  }, []);

  const refreshGPS = useCallback(async () => {
    try {
      const g = await getGPSOnce();
      setGps(g);
    } catch (e) {
      setGps(null); setFaError((prev) => prev || `GPS: ${e.message || "gagal"}`);
    }
  }, []);

  useEffect(() => { initModel(); refreshGPS(); }, [initModel, refreshGPS]);
  useEffect(() => () => { stopStream(stream); }, [stream]);
  // Refresh GPS setiap 60 detik saat kiosk aktif
  useEffect(() => {
    if (!running) return;
    const id = setInterval(refreshGPS, 60000);
    return () => clearInterval(id);
  }, [running, refreshGPS]);

  const startCam = async () => {
    try {
      const s = await getCamera(videoRef.current, { facingMode: "user" });
      setStream(s);
      setRunning(true);
    } catch (e) { setFaError(e.message || "Gagal buka kamera"); }
  };
  const stopCam = () => { stopStream(stream); setStream(null); setRunning(false); setScanning(false); };

  // Scan loop: setiap 2-3 detik, deteksi wajah dan kirim ke backend
  useEffect(() => {
    if (!running || !faReady || !gps) return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      // Rate limit min 2.5s antar scan
      const now = Date.now();
      if (now - lastScanRef.current < 2500) {
        setTimeout(tick, 500);
        return;
      }
      lastScanRef.current = now;
      setScanning(true);
      try {
        setStatusMsg("Mendeteksi wajah…");
        const result = await detectFace(videoRef.current);
        if (!result) {
          setStatusMsg("Arahkan wajah ke kamera");
          setScanning(false);
          setTimeout(tick, 1200);
          return;
        }
        const selfie = snapshotVideo(videoRef.current, canvasRef.current, 0.55);
        setStatusMsg("Verifikasi…");
        let resp;
        try {
          resp = await api.post("/attendance/kiosk-scan", {
            face_descriptor: Array.from(result.descriptor),
            latitude: gps.latitude, longitude: gps.longitude, accuracy_meters: gps.accuracy,
            selfie_photo_b64: selfie,
          });
        } catch (err) {
          const data = err.response?.data;
          if (err.response?.status === 404) {
            setLastResult({ ok: false, name: null, action: "unknown", detail: data?.detail || "Wajah tidak dikenali", ts: Date.now() });
            playSound("unknown");
          } else if (err.response?.status === 422 && data?.action === "cooldown") {
            const empKey = data.employee_name || "_";
            cooldownRef.current[empKey] = Date.now() + ((data.wait_minutes || 5) * 60 * 1000);
            setLastResult({ ok: false, name: data.employee_name, action: "cooldown", detail: data.detail, ts: Date.now() });
            playSound("cooldown");
          } else {
            setLastResult({ ok: false, name: null, action: "error", detail: data?.detail || err.message, ts: Date.now() });
            playSound("unknown");
          }
          setStatusMsg("");
          setScanning(false);
          // Longer pause on error / unknown face to avoid spamming
          setTimeout(tick, 3500);
          return;
        }
        const r = resp.data;
        // Cache cooldown: same employee cannot scan again for 30s
        if (r.identified && r.employee_id) {
          cooldownRef.current[r.employee_id] = Date.now() + 30000;
        }
        setLastResult({
          ok: true,
          name: r.employee_name,
          action: r.action,
          detail: r.action === "clock_in" ? `Clock-in ${r.local_ts?.slice(11, 16) || ""}${r.is_late ? " (terlambat)" : ""}${!r.inside_radius ? " · di luar radius" : ""}`
                : r.action === "clock_out" ? `Clock-out ${r.local_ts?.slice(11, 16) || ""} · ${r.work_hours || 0}j (OT ${r.overtime_hours || 0}j)`
                : r.detail || "",
          ts: Date.now(),
        });
        // Iter v34cg — Audio feedback berbeda per action
        if (r.action === "clock_in") playSound("success_in");
        else if (r.action === "clock_out") playSound("success_out");
        else if (r.action === "already_done") playSound("already");
        setStatusMsg("");
      } catch (e) {
        setLastResult({ ok: false, name: null, action: "error", detail: e.message, ts: Date.now() });
      } finally {
        setScanning(false);
        // Next scan after 3.5s to give feedback time
        setTimeout(tick, 3500);
      }
    };

    const t = setTimeout(tick, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [running, faReady, gps, playSound]);

  // Auto-clear result after 8s
  useEffect(() => {
    if (!lastResult) return;
    const id = setTimeout(() => setLastResult(null), 8000);
    return () => clearTimeout(id);
  }, [lastResult]);

  const canRun = faReady && gps && stream;

  return (
    <div className="fixed inset-0 bg-stone-900 text-stone-100 flex flex-col" data-testid="absen-kiosk-page">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-3 bg-stone-950 border-b border-stone-800">
        <div className="flex items-center gap-3">
          <ScanFace className="w-6 h-6 text-terra-400" />
          <div>
            <h1 className="font-display text-lg font-semibold">Absen Kiosk · Face Scan</h1>
            <p className="text-[11px] text-stone-400">Karyawan cukup menghadap kamera. Sistem otomatis mendeteksi & mencatat absen.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge className={faReady ? "bg-emerald-700 text-emerald-100" : "bg-stone-700 text-stone-300"} data-testid="kiosk-face-status">
            Model: {faReady ? "siap" : "loading"}
          </Badge>
          <Badge className={gps ? "bg-emerald-700 text-emerald-100" : "bg-red-700 text-red-100"} data-testid="kiosk-gps-status">
            <MapPin className="w-3 h-3 mr-1 inline" /> GPS: {gps ? `±${Math.round(gps.accuracy)}m` : "off"}
          </Badge>
          <Badge className={running ? "bg-emerald-700 text-emerald-100" : "bg-stone-700 text-stone-300"}>
            {running ? (scanning ? "Scanning…" : "Idle") : "Berhenti"}
          </Badge>
          <Badge className={audioOn ? "bg-sky-700 text-sky-100" : "bg-stone-700 text-stone-300"} data-testid="kiosk-audio-status">
            {audioOn ? <Volume2 className="w-3 h-3 mr-1 inline" /> : <VolumeX className="w-3 h-3 mr-1 inline" />}
            Suara: {audioOn ? "on" : "off"}
          </Badge>
          {/* Iter v34ci — Fullscreen toggle */}
          {!isFullscreen ? (
            <button
              onClick={enterFullscreen}
              className="ml-1 px-2 py-1 rounded-full bg-terra-600 hover:bg-terra-700 text-white text-[11px] flex items-center gap-1"
              data-testid="kiosk-fullscreen-btn"
              title="Aktifkan mode Fullscreen"
            >
              <Maximize2 className="w-3 h-3" /> Fullscreen
            </button>
          ) : (
            <button
              onClick={requestExitFullscreen}
              className="ml-1 px-2 py-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-[11px] flex items-center gap-1"
              data-testid="kiosk-exit-fullscreen-btn"
              title={pinRequired ? "Butuh PIN untuk keluar" : "Keluar Fullscreen"}
            >
              {pinRequired ? <Lock className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />} Exit
            </button>
          )}
          <Link to="/absensi" className="text-[11px] text-stone-400 hover:text-stone-200 underline">Ke Admin →</Link>
        </div>
      </header>

      {/* Main */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Camera area */}
        <div className="flex-1 relative bg-black flex items-center justify-center">
          <video
            ref={videoRef}
            className="max-h-full max-w-full object-contain"
            style={{ transform: "scaleX(-1)" }}
            playsInline muted autoPlay
            data-testid="kiosk-video"
          />
          <canvas ref={canvasRef} className="hidden" />

          {/* Center reticle */}
          {running && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className={`w-72 h-72 md:w-96 md:h-96 border-4 rounded-full ${scanning ? "border-terra-400 animate-pulse" : "border-stone-500/40"}`} />
            </div>
          )}

          {/* Status overlay */}
          <div className="absolute bottom-4 left-4 right-4 flex flex-col items-center gap-2">
            {statusMsg && (
              <p className="text-sm text-stone-300 bg-stone-900/70 px-3 py-1 rounded-full" data-testid="kiosk-status-msg">
                {statusMsg}
              </p>
            )}
            {faError && (
              <p className="text-sm text-red-300 bg-red-900/70 px-3 py-1 rounded-full flex items-center gap-1" data-testid="kiosk-face-error">
                <AlertTriangle className="w-3 h-3" /> {faError}
              </p>
            )}
          </div>

          {/* Result banner */}
          {lastResult && (
            <div className={`absolute top-4 left-1/2 -translate-x-1/2 max-w-md w-[90%] px-5 py-4 rounded-xl shadow-2xl border-2 ${
              lastResult.ok && lastResult.action === "clock_in" ? "bg-emerald-600 border-emerald-400 text-white" :
              lastResult.ok && lastResult.action === "clock_out" ? "bg-sky-600 border-sky-400 text-white" :
              lastResult.ok && lastResult.action === "already_done" ? "bg-amber-600 border-amber-400 text-white" :
              lastResult.action === "cooldown" ? "bg-amber-700 border-amber-500 text-white" :
              "bg-red-700 border-red-500 text-white"
            }`} data-testid="kiosk-result-banner">
              <div className="flex items-start gap-3">
                {lastResult.ok && lastResult.action === "clock_in" && <LogIn className="w-8 h-8 shrink-0" />}
                {lastResult.ok && lastResult.action === "clock_out" && <LogOut className="w-8 h-8 shrink-0" />}
                {lastResult.ok && lastResult.action === "already_done" && <CheckCircle2 className="w-8 h-8 shrink-0" />}
                {lastResult.action === "cooldown" && <Clock className="w-8 h-8 shrink-0" />}
                {!lastResult.ok && lastResult.action !== "cooldown" && <XCircle className="w-8 h-8 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-2xl font-bold truncate" data-testid="kiosk-result-name">
                    {lastResult.name || "Wajah tidak dikenali"}
                  </p>
                  <p className="text-sm opacity-90" data-testid="kiosk-result-detail">
                    {lastResult.detail}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Side control panel */}
        <aside className="w-full md:w-80 bg-stone-950 border-l border-stone-800 p-5 flex flex-col gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Kontrol</p>
            {!running ? (
              <Button onClick={startCam} disabled={!faReady || !gps} className="w-full !bg-emerald-600 hover:!bg-emerald-700 !text-white flex items-center justify-center gap-2 disabled:!bg-stone-700 disabled:!cursor-not-allowed" data-testid="kiosk-start-btn">
                <Play className="w-4 h-4" /> Mulai Kiosk
              </Button>
            ) : (
              <Button onClick={stopCam} className="w-full !bg-red-600 hover:!bg-red-700 !text-white flex items-center justify-center gap-2" data-testid="kiosk-stop-btn">
                <Pause className="w-4 h-4" /> Berhenti
              </Button>
            )}
            {/* Iter v34cg — Toggle audio notification */}
            <button
              type="button"
              onClick={() => {
                const next = !audioOn;
                setAudioOn(next);
                if (next) {
                  // Test voice on enable (also unblocks audio autoplay lock)
                  speakVoice("Voice notification enabled", { volume: audioVolume });
                }
              }}
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-xs rounded-md border border-stone-700 hover:border-stone-500 hover:bg-stone-800 transition"
              data-testid="kiosk-audio-toggle"
            >
              {audioOn ? <Volume2 className="w-4 h-4 text-emerald-400" /> : <VolumeX className="w-4 h-4 text-stone-400" />}
              <span className={audioOn ? "text-emerald-300" : "text-stone-400"}>{audioOn ? "Suara Notifikasi: AKTIF" : "Suara Notifikasi: NONAKTIF"}</span>
            </button>
            {/* Iter v34cj — Volume slider TTS voice */}
            {audioOn && (
              <div className="mt-2 px-1" data-testid="kiosk-volume-wrap">
                <label className="flex items-center justify-between text-[10px] uppercase tracking-widest text-stone-500 mb-1">
                  <span>Volume Suara</span>
                  <span className="text-emerald-400 font-mono">{Math.round(audioVolume * 100)}%</span>
                </label>
                <input
                  type="range" min="0" max="1" step="0.05" value={audioVolume}
                  onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                  onMouseUp={() => speakVoice(VOICE_PHRASES.granted, { volume: audioVolume })}
                  onTouchEnd={() => speakVoice(VOICE_PHRASES.granted, { volume: audioVolume })}
                  className="w-full accent-terra-500"
                  data-testid="kiosk-volume-slider"
                />
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Info</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-stone-400">Operator</span><span className="text-stone-200 truncate max-w-[180px]">{user?.email || user?.name || "-"}</span></div>
              <div className="flex justify-between"><span className="text-stone-400">Model</span><span className="text-stone-200">{faReady ? "siap" : (faError ? "gagal" : "loading")}</span></div>
              <div className="flex justify-between"><span className="text-stone-400">Perangkat</span><span className="text-stone-200">{IS_LOW_END ? "low-end" : "normal"}</span></div>
              <div className="flex justify-between"><span className="text-stone-400">GPS akurasi</span><span className="text-stone-200">{gps ? `±${Math.round(gps.accuracy)}m` : "-"}</span></div>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Petunjuk</p>
            <ol className="text-[11px] text-stone-300 space-y-1 list-decimal list-inside">
              <li>Klik <b>Mulai Kiosk</b> — model & GPS aktif.</li>
              <li>Karyawan berdiri ~50cm dari kamera.</li>
              <li>Sistem otomatis mendeteksi & clock-in.</li>
              <li>Untuk clock-out, karyawan menghadap kamera lagi setelah min. 30 menit.</li>
            </ol>
          </div>

          <div className="mt-auto flex gap-2">
            <Button onClick={initModel} className="!text-xs !bg-stone-800 hover:!bg-stone-700 !text-stone-100 flex-1 flex items-center justify-center gap-1" data-testid="kiosk-reload-model">
              <RefreshCw className="w-3 h-3" /> Reload Model
            </Button>
            <Button onClick={refreshGPS} className="!text-xs !bg-stone-800 hover:!bg-stone-700 !text-stone-100 flex-1 flex items-center justify-center gap-1" data-testid="kiosk-reload-gps">
              <MapPin className="w-3 h-3" /> Refresh GPS
            </Button>
          </div>
        </aside>
      </div>

      {/* Iter v34ci — PIN Lock Overlay */}
      {locked && (
        <div className="fixed inset-0 z-[9999] bg-stone-900/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="kiosk-pin-overlay">
          <div className="max-w-sm w-full bg-stone-800 border border-stone-700 rounded-2xl p-6 shadow-2xl">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="h-14 w-14 rounded-full bg-red-600/20 flex items-center justify-center">
                <Lock className="h-7 w-7 text-red-400" />
              </div>
              <h2 className="text-xl font-display font-semibold text-stone-100">Halaman Kiosk Terkunci</h2>
              <p className="text-xs text-stone-400">Masukkan PIN 6 digit yang telah diset oleh admin di Pengaturan Database.</p>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={pinInput}
                onChange={(e) => { setPinInput(e.target.value.replace(/\D/g, "").slice(0, 6)); setPinError(""); }}
                onKeyDown={(e) => e.key === "Enter" && submitPin()}
                autoFocus
                className="w-full mt-2 text-center text-3xl tracking-[0.6em] font-mono bg-stone-950 border-2 border-stone-700 rounded-xl py-3 text-stone-100 focus:border-terra-500 focus:outline-none"
                placeholder="••••••"
                data-testid="kiosk-pin-overlay-input"
              />
              {pinError && <p className="text-xs text-red-400" data-testid="kiosk-pin-error">{pinError}</p>}
              <button
                onClick={submitPin}
                disabled={pinChecking || pinInput.length !== 6}
                className="w-full py-2.5 rounded-xl bg-terra-600 hover:bg-terra-700 disabled:bg-stone-700 disabled:cursor-not-allowed text-white text-sm font-semibold"
                data-testid="kiosk-pin-unlock"
              >
                {pinChecking ? "Memverifikasi…" : "Buka Kunci"}
              </button>
              <p className="text-[10px] text-stone-500 italic mt-2">Kiosk tetap aktif memantau di belakang layar.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
